import React, { useEffect, useState } from 'react';
import { Dropdown } from 'semantic-ui-react';
import { useDebouncedCallback } from 'use-debounce';

import useTranslate from '@/hooks/useTranslate';
import {
    useGetEaccountingArticleQuery,
    useLazySearchEaccountingArticlesQuery,
} from '@/services/bmApi';
import { EaccountingArticle } from '@/types';

const SEARCH_DEBOUNCE_MS = 300;

interface Props {
    value?: string;
    onChange: (articleId: string | null) => void;
    vatPercent?: number;
    upward?: boolean;
    'data-cy'?: string;
}

const ArticleSearchSelect: React.FC<Props> = ({
    value,
    onChange,
    vatPercent,
    upward,
    'data-cy': dataCy,
}) => {
    const { translate } = useTranslate();
    const [search, searchState] = useLazySearchEaccountingArticlesQuery();
    const [opened, setOpened] = useState(false);
    const selected = useGetEaccountingArticleQuery(value ?? '', { skip: !value });

    const [debouncedSearch] = useDebouncedCallback((query: string) => {
        void search({ Search: query }, true);
    }, SEARCH_DEBOUNCE_MS);

    useEffect(() => {
        if (opened) void search({ Search: '' }, true);
    }, [opened, search]);

    const found = searchState.data ?? [];
    // Prefer articles whose VAT matches, but never leave the user with nothing to pick.
    const matchingVat =
        vatPercent === undefined
            ? found
            : found.filter((a) => parseFloat(a.VatRatePercent ?? '') === vatPercent);
    const visible = matchingVat.length > 0 ? matchingVat : found;

    const articles: EaccountingArticle[] = [...visible];
    if (selected.data && !articles.some((a) => a.ArticleId === selected.data?.ArticleId)) {
        articles.unshift(selected.data);
    }

    const vatLabel = translate('priceArticleMappingsTable.optionText.VAT');
    const priceLabel = translate('priceArticleMappingsTable.optionText.price');

    const options = articles.map((article) => {
        const details: string[] = [];
        if (article.VatRate) details.push(`${vatLabel}: ${article.VatRate}`);
        if (article.VatRate && article.ArticlePrice) {
            details.push(`${priceLabel}: ${article.ArticlePrice}${article.CurrencySign ?? ''}`);
        }

        return {
            key: article.ArticleId,
            value: article.ArticleId as string,
            text: [article.ArticleName, ...(details.length ? [details.join(', ')] : [])].join(' | '),
        };
    });

    return (
        <Dropdown
            selection
            search={(opts) => opts}
            clearable
            fluid
            upward={upward}
            data-cy={dataCy}
            value={value ?? ''}
            options={options}
            loading={searchState.isFetching || selected.isFetching}
            placeholder={translate('eaccounting.articleSearch.placeholder') as string}
            noResultsMessage={translate('eaccounting.articleSearch.noResults') as string}
            onOpen={() => setOpened(true)}
            onSearchChange={(e, data) => debouncedSearch(data.searchQuery)}
            onChange={(e, data) =>
                onChange(typeof data.value === 'string' && data.value ? data.value : null)
            }
        />
    );
};

export default ArticleSearchSelect;
